import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import LoadSpinner from "../../../utilities/LoadSpinner";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_board,
  API_get_board_members,
  API_create_board_list,
  API_update_board_list,
  API_move_board_list,
  API_archive_board_list,
  API_create_board_card,
  API_move_board_card,
  API_archive_board_card,
  API_duplicate_board_card,
} from "../../../constants";
import BoardColumn from "./BoardColumn";
import BoardCardModal from "./BoardCardModal";
import ConfirmDialog from "./ConfirmDialog";
import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";


/* Inline composer for adding a new list at the right edge of the board. */
function AddListComposer({ onAdd }) {
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setDraft("");
  };

  if (!isAdding) {
    return (
      <button
        onClick={() => setIsAdding(true)}
        className="w-72 shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-medium text-slate-600"
      >
        <AddIcon style={{ fontSize: 18 }} />
        Add a list
      </button>
    );
  }

  return (
    <div className="w-72 shrink-0 bg-slate-100 rounded-xl p-2">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Enter list name…"
        maxLength={255}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          } else if (e.key === "Escape") {
            setIsAdding(false);
            setDraft("");
          }
        }}
        className="w-full text-sm text-slate-800 bg-white border border-violet-400 rounded-lg px-2.5 py-2 outline-none"
      />
      <div className="flex items-center gap-2 mt-1.5">
        <button
          className="px-3 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
          onClick={submit}
        >
          Add list
        </button>
        <button
          className="text-slate-500 hover:text-slate-700"
          onClick={() => {
            setIsAdding(false);
            setDraft("");
          }}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
}

function BoardsDisplay(props) {
  // The board follows the global home store (shared header store dropdown).
  const selectedStore = props.homeStore;
  const [board, setBoard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [boardMembers, setBoardMembers] = useState([]);
  const [activeCardUuid, setActiveCardUuid] = useState(null);
  const [pendingArchive, setPendingArchive] = useState(null);

  // Live mirror of selectedStore — async fetches compare against it to
  // discard responses that resolve after the store selector has moved on.
  const selectedStoreRef = useRef(selectedStore);
  useEffect(() => {
    selectedStoreRef.current = selectedStore;
  }, [selectedStore]);

  // Store memberships the header dropdown may offer: Boards enabled and a
  // manager/admin membership there (the board API requires both). Passing
  // every membership would let a manager pick a board-disabled store (the
  // tab then gates off) or an employee-only store (403s).
  const boardStoreOptions = useMemo(() => {
    const enabledIds = new Set(
      (props.storeNames || []).filter((s) => s.boards_enabled).map((s) => s.id)
    );
    return (props.storeOptions || []).filter(
      (m) =>
        enabledIds.has(m.store) &&
        ["company_manager", "company_admin", "rightwork_internal"].includes(
          m.permission_type
        )
    );
  }, [props.storeNames, props.storeOptions]);
  const homeStoreEligible = boardStoreOptions.some(
    (m) => m.store === selectedStore
  );
  // What the header dropdown offers. When the home store is ineligible it
  // is included too (as the current selection) so StoreName still renders
  // a dropdown even if there is only one eligible store to move to -
  // otherwise the "pick a store" message points at a selector that never
  // opens (StoreName is plain text below two options).
  const selectorStoreOptions = useMemo(() => {
    if (homeStoreEligible) return boardStoreOptions;
    const homeMembership = (props.storeOptions || []).find(
      (m) => m.store === selectedStore
    );
    return homeMembership
      ? [homeMembership, ...boardStoreOptions]
      : boardStoreOptions;
  }, [homeStoreEligible, boardStoreOptions, props.storeOptions, selectedStore]);

  // The selected store's company. Uploads must be scoped to it — the user's
  // home company can differ when viewing a board in another company.
  const selectedStoreCompany = useMemo(() => {
    const store = (props.storeNames || []).find((s) => s.id === selectedStore);
    return store ? store.company : props.homeCompany;
  }, [props.storeNames, selectedStore, props.homeCompany]);

  const fetchBoard = useCallback(
    async (showLoading = true) => {
      if (showLoading) setIsLoading(true);
      try {
        const res = await getRightworkAPI(API_get_board, { store_id: selectedStore });
        // Drop the response if the store selector changed while it was in
        // flight — otherwise a stale store's board would clobber the current one.
        if (selectedStore !== selectedStoreRef.current) return;
        setBoard(res.data?.redux?.payload?.board || null);
      } catch (error) {
        if (selectedStore !== selectedStoreRef.current) return;
        toast.error("Failed to load board");
      } finally {
        if (showLoading && selectedStore === selectedStoreRef.current) {
          setIsLoading(false);
        }
      }
    },
    [selectedStore]
  );

  const fetchBoardMembers = useCallback(async () => {
    try {
      const res = await getRightworkAPI(API_get_board_members, {
        store_id: selectedStore,
      });
      // Same stale-response guard as fetchBoard.
      if (selectedStore !== selectedStoreRef.current) return;
      setBoardMembers(res.data?.redux?.payload?.board_members || []);
    } catch (error) {
      // Non-fatal — the member picker simply shows no options.
    }
  }, [selectedStore]);

  useEffect(() => {
    if (!homeStoreEligible) {
      setBoard(null);
      setIsLoading(false);
      return;
    }
    fetchBoard();
    fetchBoardMembers();
  }, [fetchBoard, fetchBoardMembers, homeStoreEligible]);

  /* ----- List handlers ----- */

  const handleCreateList = async (name) => {
    try {
      const res = await postRightworkAPI(API_create_board_list, {
        store_id: selectedStore,
        name,
      });
      const newList = res.data?.redux?.payload?.board_list;
      if (newList) {
        setBoard((prev) => ({
          ...prev,
          lists: [...prev.lists, { ...newList, cards: newList.cards || [] }],
        }));
      }
    } catch (error) {
      toast.error("Failed to create list");
    }
  };

  const handleRenameList = async (listUuid, name) => {
    setBoard((prev) => ({
      ...prev,
      lists: prev.lists.map((l) => (l.uuid === listUuid ? { ...l, name } : l)),
    }));
    try {
      await postRightworkAPI(API_update_board_list, {
        store_id: selectedStore,
        board_list_uuid: listUuid,
        name,
      });
    } catch (error) {
      toast.error("Failed to rename list");
      fetchBoard(false);
    }
  };

  const handleArchiveList = async (listUuid) => {
    const prevBoard = board;
    setBoard((prev) => ({
      ...prev,
      lists: prev.lists.filter((l) => l.uuid !== listUuid),
    }));
    try {
      await postRightworkAPI(API_archive_board_list, {
        store_id: selectedStore,
        board_list_uuid: listUuid,
      });
      toast.success("List archived");
    } catch (error) {
      toast.error("Failed to archive list");
      setBoard(prevBoard);
    }
  };

  /* ----- Card handlers ----- */

  const handleCreateCard = async (listUuid, title) => {
    // A placeholder card shown immediately; reconciled with the saved card
    // (or removed) once the API responds. The temp uuid is replaced by the
    // real one — handleCardClick ignores temp cards until then.
    const tempUuid = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimisticCard = {
      uuid: tempUuid,
      card_number: null,
      board_list_uuid: listUuid,
      title,
      description: "",
      position: 0,
      due_date: null,
      due_complete: false,
      created_by: null,
      labels: [],
      members: [],
      file_uploads: [],
      checklists: [],
      comment_count: 0,
    };
    setBoard((prev) => ({
      ...prev,
      lists: prev.lists.map((l) =>
        l.uuid === listUuid
          ? { ...l, cards: [...(l.cards || []), optimisticCard] }
          : l
      ),
    }));
    try {
      const res = await postRightworkAPI(API_create_board_card, {
        store_id: selectedStore,
        board_list_uuid: listUuid,
        title,
      });
      const newCard = res.data?.redux?.payload?.board_card;
      if (newCard) {
        // Swap the placeholder for the saved card, wherever it now sits.
        setBoard((prev) => ({
          ...prev,
          lists: prev.lists.map((l) => ({
            ...l,
            cards: (l.cards || []).map((c) =>
              c.uuid === tempUuid ? newCard : c
            ),
          })),
        }));
      }
    } catch (error) {
      toast.error("Failed to create card");
      // Roll back — drop the placeholder.
      setBoard((prev) => ({
        ...prev,
        lists: prev.lists.map((l) => ({
          ...l,
          cards: (l.cards || []).filter((c) => c.uuid !== tempUuid),
        })),
      }));
    }
  };

  const handleArchiveCard = async (cardUuid) => {
    const prevBoard = board;
    // Remove the card from whichever list holds it — don't rely on a passed-in
    // list id, which can be stale right after an optimistic drag.
    setBoard((prev) => ({
      ...prev,
      lists: prev.lists.map((l) => ({
        ...l,
        cards: (l.cards || []).filter((c) => c.uuid !== cardUuid),
      })),
    }));
    try {
      await postRightworkAPI(API_archive_board_card, {
        store_id: selectedStore,
        board_card_uuid: cardUuid,
      });
    } catch (error) {
      toast.error("Failed to archive card");
      setBoard(prevBoard);
    }
  };

  const handleDuplicateCard = async (sourceCardUuid) => {
    // Duplicating clones attachments too, which can take a moment — show progress.
    const toastId = toast.loading("Duplicating card…", { autoClose: false });
    try {
      const res = await postRightworkAPI(API_duplicate_board_card, {
        store_id: selectedStore,
        board_card_uuid: sourceCardUuid,
      });
      const newCard = res.data?.redux?.payload?.board_card;
      if (newCard) {
        // Insert the copy directly after the original.
        setBoard((prev) => ({
          ...prev,
          lists: prev.lists.map((l) => {
            const idx = (l.cards || []).findIndex(
              (c) => c.uuid === sourceCardUuid
            );
            if (idx === -1) return l;
            const cards = [...l.cards];
            cards.splice(idx + 1, 0, newCard);
            return { ...l, cards };
          }),
        }));
      }
      toast.dismiss(toastId);
      toast.success("Card duplicated", { autoClose: 3000 });
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to duplicate card");
    }
  };

  /* ----- Card detail modal ----- */

  const handleCardClick = (card) => {
    // Ignore clicks on a not-yet-saved (optimistic) card.
    if (String(card.uuid).startsWith("temp-")) return;
    setActiveCardUuid(card.uuid);
  };

  const closeModal = () => {
    setActiveCardUuid(null);
  };

  // Replace a card wherever it lives in the board after an edit.
  const handleCardUpdated = (updatedCard) => {
    setBoard((prev) => ({
      ...prev,
      lists: prev.lists.map((l) => ({
        ...l,
        cards: (l.cards || []).map((c) =>
          c.uuid === updatedCard.uuid ? updatedCard : c
        ),
      })),
    }));
  };

  const handleLabelCreated = (label) => {
    setBoard((prev) => ({
      ...prev,
      labels: [...(prev.labels || []), label],
    }));
  };

  /* ----- Store switching (via the shared header store dropdown) ----- */

  useEffect(() => {
    setActiveCardUuid(null);
    setPendingArchive(null);
  }, [selectedStore]);

  /* ----- Archive confirmation ----- */

  const requestArchiveCard = (cardUuid) => {
    let name = "this card";
    for (const list of board?.lists || []) {
      const c = (list.cards || []).find((x) => x.uuid === cardUuid);
      if (c) {
        name = c.title;
        break;
      }
    }
    setPendingArchive({ kind: "card", uuid: cardUuid, name });
  };

  const requestArchiveList = (listUuid) => {
    const list = (board?.lists || []).find((x) => x.uuid === listUuid);
    setPendingArchive({
      kind: "list",
      uuid: listUuid,
      name: list ? list.name : "this list",
    });
  };

  const confirmArchive = () => {
    if (!pendingArchive) return;
    if (pendingArchive.kind === "card") {
      handleArchiveCard(pendingArchive.uuid);
      closeModal();
    } else {
      handleArchiveList(pendingArchive.uuid);
    }
    setPendingArchive(null);
  };

  /* ----- Drag and drop ----- */

  const onDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (type === "list") {
      const newLists = [...board.lists];
      const [moved] = newLists.splice(source.index, 1);
      newLists.splice(destination.index, 0, moved);
      setBoard((prev) => ({ ...prev, lists: newLists }));
      try {
        await postRightworkAPI(API_move_board_list, {
          store_id: selectedStore,
          board_list_uuid: draggableId,
          new_index: destination.index,
        });
      } catch (error) {
        toast.error("Failed to move list");
        fetchBoard(false);
      }
      return;
    }

    // Card move — shallow-clone each list's card array so we don't mutate state.
    const newLists = board.lists.map((l) => ({ ...l, cards: [...(l.cards || [])] }));
    const sourceList = newLists.find((l) => l.uuid === source.droppableId);
    const destList = newLists.find((l) => l.uuid === destination.droppableId);
    if (!sourceList || !destList) return;
    const [movedCard] = sourceList.cards.splice(source.index, 1);
    if (!movedCard) return;
    // Keep the card's denormalized list pointer in sync with the move so any
    // later read of board_list_uuid (e.g. the modal) sees the correct list.
    destList.cards.splice(destination.index, 0, {
      ...movedCard,
      board_list_uuid: destList.uuid,
    });
    setBoard((prev) => ({ ...prev, lists: newLists }));
    try {
      await postRightworkAPI(API_move_board_card, {
        store_id: selectedStore,
        board_card_uuid: draggableId,
        target_list_uuid: destination.droppableId,
        new_index: destination.index,
      });
    } catch (error) {
      toast.error("Failed to move card");
      fetchBoard(false);
    }
  };

  /* ----- Render ----- */

  if (!props.boardsEnabled) return null;

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center">
        <LoadSpinner />
      </div>
    );
  }

  // Resolve the open card (and the list it sits in) live from board state so
  // edits made in the modal reflect immediately.
  let activeCard = null;
  let activeListName = "";
  if (activeCardUuid && board) {
    for (const list of board.lists) {
      const found = (list.cards || []).find((c) => c.uuid === activeCardUuid);
      if (found) {
        activeCard = found;
        activeListName = list.name;
        break;
      }
    }
  }

  return (
    <div className="w-full h-screen bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 my-5">
        <OperationsPageHeader
          title="Planning Board"
          scope="store"
          homeStore={props.homeStore}
          storeOptions={selectorStoreOptions}
          storeNames={props.storeNames}
          change_home_store={props.change_home_store}
        />
      </div>

      {/* Board */}
      <div className="flex-1 overflow-x-auto bg-white">
        {!homeStoreEligible ? (
          <div className="px-10 py-16 text-center">
            <p className="text-slate-500">
              Planning Board isn&apos;t available for you at this store.
              {boardStoreOptions.length > 0 &&
                " Pick a store from the dropdown above."}
            </p>
          </div>
        ) : !board ? (
          <div className="px-10 py-16 text-center">
            <p className="text-slate-500">Unable to load this board.</p>
            <button
              className="mt-3 px-4 py-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
              onClick={() => fetchBoard()}
            >
              Try again
            </button>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex items-start gap-3 px-10 py-5">
              <Droppable droppableId="board" direction="horizontal" type="list">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="flex items-start gap-3"
                  >
                    {board.lists.map((list, idx) => (
                      <BoardColumn
                        key={list.uuid}
                        list={list}
                        index={idx}
                        onAddCard={handleCreateCard}
                        onRenameList={handleRenameList}
                        onArchiveList={requestArchiveList}
                        onCardClick={handleCardClick}
                        onArchiveCard={requestArchiveCard}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
              <AddListComposer onAdd={handleCreateList} />
            </div>
          </DragDropContext>
        )}
      </div>

      {activeCard && (
        <BoardCardModal
          key={activeCard.uuid}
          card={activeCard}
          listName={activeListName}
          storeId={selectedStore}
          companyId={selectedStoreCompany}
          boardLabels={board.labels}
          boardMembers={boardMembers}
          onClose={closeModal}
          onCardUpdated={handleCardUpdated}
          onArchive={() => requestArchiveCard(activeCard.uuid)}
          onDuplicate={() => {
            handleDuplicateCard(activeCard.uuid);
            closeModal();
          }}
          onLabelCreated={handleLabelCreated}
          onBoardNeedsRefresh={() => fetchBoard(false)}
        />
      )}

      <ConfirmDialog
        open={!!pendingArchive}
        title={
          pendingArchive?.kind === "list"
            ? "Archive this list?"
            : "Archive this card?"
        }
        message={
          pendingArchive?.kind === "list"
            ? `"${pendingArchive?.name}" and all of its cards will be archived.`
            : `"${pendingArchive?.name}" will be archived.`
        }
        confirmLabel="Archive"
        onConfirm={confirmArchive}
        onCancel={() => setPendingArchive(null)}
      />
    </div>
  );
}

export default BoardsDisplay;
